import React, { useId, useRef, useState } from 'react';

/**
 * "أدخل عدد الأسئلة التي تريد مراجعتها" — the ring on the wizard's last step,
 * nodes 16370:191845 to 16370:191860.
 *
 * The file exports the track, the arc and the thumb as three fixed shapes at
 * 43 of 125, so they are drawn here from that geometry instead: a 190.324px box,
 * a gray/300 track and a brand arc both 11.419 wide with the outer edge on the
 * box, and a 17.3px white thumb ringed 2.595 in the brand with the file's own
 * drop shadow. The odd numbers are the frame's scale, as on `Card`.
 *
 * The arc runs from twelve o'clock, clockwise, over the whole of `max` — the
 * exam's cap — which is how the frame draws 43 at a little past a third.
 * The value itself can go no higher than `ceiling`, the questions actually
 * found. It does not mirror in English: a dial is not a reading-order element.
 *
 * THREE WAYS TO SET IT
 *
 *  - drag or click the ring. Dragging across twelve o'clock holds the end the
 *    drag came from rather than jumping to the other;
 *  - the "−" and "+" discs, or the arrow, Page and Home/End keys on the ring;
 *  - type into the number in the middle, which is a field. The label asks the
 *    reader to *enter* a number, and a ring alone makes 137 of 500 a fiddle.
 *
 * The scale under the ring reads from the lowest count that can start an exam
 * to the cap. The frame letters its low end "5"; the old wizard allowed one
 * question, and a topic with three should still be startable.
 */
const SIZE = 190.324;
const CENTER = SIZE / 2;
const TRACK_WIDTH = 11.4194;
const RADIUS = CENTER - TRACK_WIDTH / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const THUMB_RADIUS = 8.65107;
const THUMB_RING_RADIUS = 7.35341;
const THUMB_RING_WIDTH = 2.59532;

const KEY_STEPS = { ArrowUp: 1, ArrowRight: 1, ArrowDown: -1, ArrowLeft: -1, PageUp: 10, PageDown: -10 };

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const StepButton = ({ label, className = '', children, ...rest }) => (
    <button
        type="button"
        aria-label={label}
        className={
            'tw-flex tw-size-[41.525px] tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full ' +
            'tw-border-[0.865px] tw-border-solid tw-border-v2-ground-400 tw-bg-v2-ground-150 tw-p-0 tw-text-[20.763px] ' +
            'tw-font-bold tw-leading-none tw-transition-colors enabled:hover:tw-bg-v2-ground-400 ' +
            'disabled:tw-cursor-not-allowed disabled:tw-opacity-40 ' +
            `focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand ${className}`
        }
        {...rest}
    >
        <span aria-hidden>{children}</span>
    </button>
);

const CountRing = ({ value, min = 1, ceiling, max, onChange, label, unit, increaseLabel, decreaseLabel }) => {
    const filterId = `v2-count-ring-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
    const ringRef = useRef(null);
    const dragFraction = useRef(null);
    const [draft, setDraft] = useState(null);

    const floor = Math.min(min, ceiling);
    const isDisabled = ceiling <= 0;
    const set = (next) => onChange(clamp(Math.round(next), floor, ceiling));

    const fraction = max > 0 ? clamp(value / max, 0, 1) : 0;
    const angle = fraction * 2 * Math.PI;
    const thumbX = CENTER + RADIUS * Math.sin(angle);
    const thumbY = CENTER - RADIUS * Math.cos(angle);

    const pointerOf = (event) => {
        const rect = ringRef.current.getBoundingClientRect();
        const dx = event.clientX - (rect.left + rect.width / 2);
        const dy = event.clientY - (rect.top + rect.height / 2);
        return { distance: Math.hypot(dx, dy) * (SIZE / rect.width), dx, dy };
    };

    const followPointer = (event) => {
        const { dx, dy } = pointerOf(event);
        let next = Math.atan2(dx, -dy) / (2 * Math.PI);
        if (next < 0) next += 1;
        const previous = dragFraction.current;
        if (previous !== null && Math.abs(next - previous) > 0.5) next = previous > 0.5 ? 1 : 0;
        dragFraction.current = next;
        set(next * max);
    };

    const handlePointerDown = (event) => {
        if (isDisabled) return;
        // Only the band of the ring answers; the middle belongs to the number.
        if (pointerOf(event).distance < RADIUS - TRACK_WIDTH * 2) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        event.currentTarget.focus();
        dragFraction.current = null;
        followPointer(event);
    };

    const handlePointerMove = (event) => {
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) followPointer(event);
    };

    const handlePointerEnd = (event) => {
        event.currentTarget.releasePointerCapture?.(event.pointerId);
        dragFraction.current = null;
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Home') set(floor);
        else if (event.key === 'End') set(ceiling);
        else if (KEY_STEPS[event.key]) set(value + KEY_STEPS[event.key]);
        else return;
        event.preventDefault();
    };

    const handleType = (event) => {
        const digits = event.target.value.replace(/\D/g, '');
        setDraft(digits);
        if (digits) set(Number(digits));
    };

    return (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-[17.302px]">
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-[27.683px]">
                <StepButton label={decreaseLabel} onClick={() => set(value - 1)} disabled={isDisabled || value <= floor} className="tw-text-v2-student-nav">
                    −
                </StepButton>

                <div className="tw-relative tw-size-[190.324px] tw-shrink-0">
                    <div
                        ref={ringRef}
                        role="slider"
                        tabIndex={isDisabled ? -1 : 0}
                        aria-label={label}
                        aria-valuemin={floor}
                        aria-valuemax={ceiling}
                        aria-valuenow={value}
                        aria-valuetext={`${value} ${unit}`}
                        aria-disabled={isDisabled || undefined}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerEnd}
                        onPointerCancel={handlePointerEnd}
                        onKeyDown={handleKeyDown}
                        className={
                            'tw-absolute tw-inset-0 tw-touch-none tw-rounded-full focus-visible:tw-outline-none ' +
                            'focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand focus-visible:tw-ring-offset-4 ' +
                            (isDisabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer')
                        }
                    >
                        <svg width="100%" height="100%" viewBox={`0 0 ${SIZE} ${SIZE}`} aria-hidden className="tw-block tw-overflow-visible">
                            <defs>
                                <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
                                    <feDropShadow dx="0" dy="1.73021" stdDeviation="2.59532" floodColor="#000000" floodOpacity="0.12549" />
                                </filter>
                            </defs>
                            <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none" strokeWidth={TRACK_WIDTH} className="tw-stroke-v2-ground-400" />
                            {value > 0 && (
                                <circle
                                    cx={CENTER}
                                    cy={CENTER}
                                    r={RADIUS}
                                    fill="none"
                                    strokeWidth={TRACK_WIDTH}
                                    strokeLinecap="round"
                                    strokeDasharray={CIRCUMFERENCE}
                                    strokeDashoffset={CIRCUMFERENCE * (1 - fraction)}
                                    transform={`rotate(-90 ${CENTER} ${CENTER})`}
                                    className="tw-stroke-v2-brand"
                                />
                            )}
                            <g filter={`url(#${filterId})`}>
                                <circle cx={thumbX} cy={thumbY} r={THUMB_RADIUS} className="tw-fill-white" />
                                <circle cx={thumbX} cy={thumbY} r={THUMB_RING_RADIUS} fill="none" strokeWidth={THUMB_RING_WIDTH} className="tw-stroke-v2-brand" />
                            </g>
                        </svg>
                    </div>

                    <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[3.46px]">
                        <input
                            type="text"
                            inputMode="numeric"
                            dir="ltr"
                            aria-label={label}
                            value={draft ?? String(value)}
                            onChange={handleType}
                            onBlur={() => setDraft(null)}
                            disabled={isDisabled}
                            className={
                                'tw-pointer-events-auto tw-w-[3.2ch] tw-rounded-[8px] tw-border-0 tw-bg-transparent tw-p-0 tw-text-center ' +
                                'tw-text-[41.525px] tw-font-bold tw-leading-none tw-text-v2-neutral-900 focus:tw-outline-none ' +
                                'focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand'
                            }
                        />
                        <span aria-hidden className="tw-text-[13.842px] tw-font-bold tw-leading-none tw-text-v2-neutral-400">
                            {unit}
                        </span>
                    </div>
                </div>

                <StepButton label={increaseLabel} onClick={() => set(value + 1)} disabled={isDisabled || value >= ceiling} className="tw-text-v2-brand">
                    +
                </StepButton>
            </div>

            <div aria-hidden className="tw-flex tw-w-[190.324px] tw-items-center tw-justify-between tw-text-[12.112px] tw-font-bold tw-leading-none tw-text-v2-neutral-400">
                <span>{floor}</span>
                <span>{max}</span>
            </div>
        </div>
    );
};

export default CountRing;
